import { HttpApiService } from "@/shared/services/httpApi.service";

interface Enable2FAResponse {
  secret: string;
  qrCode: string;
}

interface Verify2FAResponse {
  enabled: boolean;
  /** Plaintext recovery codes — returned once on first verify, empty otherwise. */
  recovery_codes: string[];
}

export interface ProfileUpdate {
  timezone?: string;
  preferred_language?: "es" | "en" | "pt";
  /** ISO 3166-1 alpha-2 country code, e.g. `"CO"`. Saved to `users.country`. */
  country?: string;
}

export const settingsService = {
  /**
   * Starts authenticator enrolment. The current password is always sent: the backend requires it while a
   * security PIN is set (400 `PASSWORD_REQUIRED`) and checks it whenever it arrives (401
   * `PASSWORD_INCORRECT`, counted toward the login lock), so a stolen session alone can never swap in its own
   * authenticator.
   */
  enable2FA: async (currentPassword: string): Promise<Enable2FAResponse> => {
    const response = await HttpApiService.post<Enable2FAResponse, { current_password: string }>(
      "/auth/2fa/enable",
      { current_password: currentPassword },
    );
    return response.data;
  },

  verify2FA: async (code: string): Promise<Verify2FAResponse> => {
    const response = await HttpApiService.post<Verify2FAResponse, { code: string }>("/auth/2fa/verify", {
      code,
    });
    return response.data;
  },

  disable2FA: async (code: string): Promise<void> => {
    await HttpApiService.post<void, { code: string }>("/auth/2fa/disable", {
      code,
    });
  },

  regenerateRecoveryCodes: async (): Promise<{ recovery_codes: string[] }> => {
    const response = await HttpApiService.post<{ recovery_codes: string[] }, Record<string, never>>(
      "/auth/2fa/recovery-codes/regenerate",
      {},
    );
    return response.data;
  },

  updateProfile: async (
    data: ProfileUpdate,
  ): Promise<{
    timezone?: string;
    preferred_language?: string;
    country?: string;
  }> => {
    const response = await HttpApiService.patch<
      { timezone?: string; preferred_language?: string; country?: string },
      ProfileUpdate
    >("/auth/profile", data);
    return response.data;
  },
};
